import upperFirst from "lodash/upperFirst";
import React from "react";

import { Truncated } from "~/components/truncated";
import { formatAbsoluteTime } from "~/tools/date-time/format/format-absolute-time";
import { flatObjectDeep } from "~/tools/flat-object-deep";
import { isTruthy } from "~/tools/is-truthy";
import { toEntries } from "~/tools/to-entries";

import { isEventRelation } from "../../backend/EventsApi";
import { EventsItemIcon } from "../../bottom_content/view/event-perspective/events-items/EventsItemIcon";
import { TableRow } from "../../bottom_content/view/panes/east/element-details/modal-dialog/TableRow";
import type { TopologyEvent } from "../../common/openapi";
import { Widget } from "../_widget";
import { DrawerTitle, ExplorationDrawer } from "../react/drawer/ExplorationDrawer";
import { Link } from "../react/link";
import { SortedTagList } from "../react/sorted-tag-list/SortedTagList";
import { Tooltip } from "../react/tooltip/Tooltip";

export interface EventPropertiesDrawerProps {
	when: undefined | TopologyEvent;
}

export const EventPropertiesDrawer: React.FC<EventPropertiesDrawerProps> = ({ when }) => (
	<ExplorationDrawer
		data-testid="event-properties-drawer"
		headerTitle={(event) => (
			<DrawerTitle
				icon={<EventsItemIcon category={event.category} className="size-6" />}
				title={event.name}
				typeOfData="Event properties"
			/>
		)}
		when={when}
	>
		{(event) => (
			<Widget card className="w-full" compact frameless>
				<TableRow label="Name">{event.name}</TableRow>
				<TableRow label="Description">{event.description}</TableRow>
				<TableRow label="Event type">{event.eventType}</TableRow>
				<TableRow label="Identifier">{event.identifier}</TableRow>
				<TableRow label="Event time">{formatAbsoluteTime(event.eventTime).toString()}</TableRow>
				<TableRow label="Processed time">{formatAbsoluteTime(event.processedTime).toString()}</TableRow>
				<TableRow label="Source">{event.source}</TableRow>
				<TableRow label="Source identifier">{event.sourceIdentifier}</TableRow>
				<TableRow label="Category">{event.category}</TableRow>

				<TableRow label="Elements">
					<SortedTagList items={event.elements.filter((item) => !isEventRelation(item)).map(({ name = "" }) => name)} />
				</TableRow>

				<TableRow label="Element identifiers">
					<SortedTagList items={event.elementIdentifiers} />
				</TableRow>

				<TableRow label="Source links">
					{event.sourceLinks.map(({ url, title }) => (
						<Tooltip key={url} title={title}>
							<Link className="max-w-full" el="a" href={url} theme="primary">
								<Truncated>{title}</Truncated>
							</Link>
						</Tooltip>
					))}
				</TableRow>

				<TableRow label="Tags">
					<SortedTagList items={event.tags.map(({ key, value }) => [key, value].filter(isTruthy).join(":"))} />
				</TableRow>

				{toEntries(flatObjectDeep({ data: event.data })).map(([key, value]) => (
					<TableRow key={key} label={upperFirst(key.replace(".", " "))}>
						{String(value)}
					</TableRow>
				))}
			</Widget>
		)}
	</ExplorationDrawer>
);
